import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Box,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  TableSortLabel,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Collapse,
  CircularProgress,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Phone as PhoneIcon,
  TrendingUp as TrendingUpIcon,
  CalendarToday as CalendarIcon,
  Settings as SettingsIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  SmartToy as AIIcon,
  Refresh as RefreshIcon,
  List as ListIcon,
  Info as InfoIcon,
  Close as CloseIcon,
  Email as EmailIcon,
  Send as SendIcon
} from '@mui/icons-material';
import Chart from 'react-apexcharts';
import NavBar from "../NavBar";
import CallTrackingForm from "./CallTrackingForm";

import DepartmentSettings from "./DepartmentSettings";
import './CallsPage.css';
import { useAuth } from "../auth/AuthProvider";

const CallsPage = () => {
  // Ref for scrolling to top
  const topOfPageRef = useRef(null);
  
  // State for modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingCallId, setEditingCallId] = useState(null);
  
  // State for department settings
  const [showDepartmentSettings, setShowDepartmentSettings] = useState(false);
  const [callSystemConfig, setCallSystemConfig] = useState(null);
  const [callSystemLoading, setCallSystemLoading] = useState(false);
  
  // State for table
  const [calls, setCalls] = useState([]);
  const [filteredCalls, setFilteredCalls] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [callSourceFilter, setCallSourceFilter] = useState('all');
  const [orderBy, setOrderBy] = useState('dateReceived');
  const [order, setOrder] = useState('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const [deepLinkProcessed, setDeepLinkProcessed] = useState(false);
  const [showDeepLinkNotification, setShowDeepLinkNotification] = useState(false);
  
  // State for resend email functionality
  const [resendEmailDialog, setResendEmailDialog] = useState(false);
  const [resendingCall, setResendingCall] = useState(null);
  const [resendingEmail, setResendingEmail] = useState(false);
  const [resendEmailResult, setResendEmailResult] = useState(null);

  // Helper function to format date for input
  const formatDateForInput = (date) => {
    const d = new Date(date);
    // Use UTC methods to avoid timezone conversion issues
    const year = d.getUTCFullYear();
    const month = String(d.getUTCMonth() + 1).padStart(2, '0');
    const day = String(d.getUTCDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // State for form
  const [callData, setCallData] = useState({
    dateReceived: formatDateForInput(new Date()),
    callerInformation: '',
    callerEmail: '',
    callerPhone: '',
    responsibleDepartment: '',
    appliesTo: '',
    locationDescription: '',
    issueDetails: '',
    status: 'Open'
  });
  const [inspections, setInspections] = useState([]);
  const [newInspection, setNewInspection] = useState({
    date: formatDateForInput(new Date()),
    descriptionOfWork: '',
    closeCall: false,
    overallCondition: 'Good'
  });
  const [showInspectionForm, setShowInspectionForm] = useState(false);
  const [editingInspectionIndex, setEditingInspectionIndex] = useState(null);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { user } = useAuth();

  // // Mock data - replace with actual API calls
  // useEffect(() => {
  //   // Generate mock call data
  //   const mockCalls = generateMockCalls();
  //   setCalls(mockCalls);
  //   setFilteredCalls(mockCalls);
  // }, []);
  // Fetch call system configuration
  const fetchCallSystemConfig = async () => {
    try {
      setCallSystemLoading(true);
      const response = await fetch(`/api/callSystem/callSystemConfig/${user.ClientID}`);
      
      if (response.ok) {
        const data = await response.json();
        // Override CityName with ClientName from user
        if (data.data && user.ClientName) {
          data.data.CityName = user.ClientName;
        }
        setCallSystemConfig(data.data);
      } else if (response.status === 404) {
        // No configuration exists yet
        setCallSystemConfig(null);
      }
    } catch (error) {
      console.error('Error fetching call system config:', error);
      setCallSystemConfig(null);
    } finally {
      setCallSystemLoading(false);
    }
  };

  // Fetch calls from API
  const fetchCalls = async () => {
    try {
      setLoading(true);
      
      const response = await fetch('/api/callSelect', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ClientID: user.ClientID
        })
      });

      if (response.ok) {
        const data = await response.json();
        // Transform the data to match our frontend structure
        const transformedCalls = data.result.map(call => ({
          id: call.CallDetailID,
          CallDetailID: call.CallDetailID,
          dateReceived: call.DateReported,
          callerInformation: call.CallerInformation,
          callerEmail: call.CallerEmail || '',
          callerPhone: call.CallerPhone || '',
          responsibleDepartment: call.ResponsibleDepartment,
          appliesTo: call.RelatedAssetType,
          locationDescription: call.LocationDescription,
          issueDetails: call.IssueDetails,
          status: call.IsClosed ? 'Closed' : 'Open',
          inspections: [], // TODO: Add inspection data when available
          isAIProcessed: call.CreatedUser && call.CreatedUser.includes('AI Assistant')
        }));
        setCalls(transformedCalls);
        setFilteredCalls(transformedCalls);
      } else {
        console.error('Failed to fetch calls');
        // Fallback to mock data if API fails
        const mockCalls = generateMockCalls();
        setCalls(mockCalls);
        setFilteredCalls(mockCalls);
      }
    } catch (error) {
      console.error('Error fetching calls:', error);
      // Fallback to mock data if API fails
      const mockCalls = generateMockCalls();
      setCalls(mockCalls);
      setFilteredCalls(mockCalls);
    } finally {
      setLoading(false);
    }
  };

  // Handle deep linking from URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const callId = urlParams.get('callId');
    const action = urlParams.get('action');
    
    console.log('Deep link check:', { callId, action, deepLinkProcessed, callsLength: calls.length });
    
    if (callId && action === 'edit' && !deepLinkProcessed) {
      // Wait for calls to be loaded, then find and edit the specific call
      const checkAndEditCall = () => {
        // Convert callId to number for comparison since CallDetailID might be numeric
        const numericCallId = parseInt(callId, 10);
        const callToEdit = calls.find(call => 
          call.CallDetailID === callId || 
          call.CallDetailID === numericCallId ||
          call.id === callId ||
          call.id === numericCallId
        );
        
        if (callToEdit) {
          console.log(`Deep link: Opening call ${callId} for editing`, callToEdit);
          loadCallForEditing(callToEdit);
          setIsModalOpen(true); // Ensure modal opens
          // Fetch inspections for the call
          fetchInspections(callToEdit.CallDetailID);
          setDeepLinkProcessed(true);
          setShowDeepLinkNotification(true);
          // Clean up URL parameters
          const newUrl = window.location.pathname;
          window.history.replaceState({}, document.title, newUrl);
          // Auto-hide notification after 5 seconds
          setTimeout(() => setShowDeepLinkNotification(false), 5000);
        } else if (calls.length > 0) {
          // Call not found in loaded calls
          console.warn(`Deep link: Call ${callId} not found in ${calls.length} loaded calls`);
          console.log('Available call IDs:', calls.map(c => ({ id: c.id, CallDetailID: c.CallDetailID })));
          setDeepLinkProcessed(true);
          // You could show an error message here
        }
      };
      
      if (calls.length > 0) {
        checkAndEditCall();
      } else {
        // If calls haven't loaded yet, set up a timeout to check again
        const timeout = setTimeout(checkAndEditCall, 2000);
        return () => clearTimeout(timeout);
      }
    }
  }, [calls, deepLinkProcessed]);

  // Mock data - replace with actual API calls
  useEffect(() => {
    fetchCalls();
    fetchCallSystemConfig();
  }, []);

  // Filter and sort calls
  useEffect(() => {
    let filtered = calls.filter(call => {
      const matchesSearch = 
        call.callerInformation.toLowerCase().includes(searchTerm.toLowerCase()) ||
        call.locationDescription.toLowerCase().includes(searchTerm.toLowerCase()) ||
        call.issueDetails.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = statusFilter === 'all' || call.status === statusFilter;
      const matchesDepartment = departmentFilter === 'all' || call.responsibleDepartment === departmentFilter;
      const matchesCallSource = callSourceFilter === 'all' || 
        (callSourceFilter === 'ai' && call.isAIProcessed) || 
        (callSourceFilter === 'manual' && !call.isAIProcessed);
      
      return matchesSearch && matchesStatus && matchesDepartment && matchesCallSource;
    });

    // Sort
    filtered.sort((a, b) => {
      const aValue = a[orderBy];
      const bValue = b[orderBy];
      
      if (order === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });

    setFilteredCalls(filtered);
    setPage(0);
  }, [calls, searchTerm, statusFilter, departmentFilter, callSourceFilter, orderBy, order]);

  // Generate mock data
  const generateMockCalls = () => {
    const departments = ['Public Works', 'Streets', 'Water', 'Parks', 'Fire Department'];
    const assetTypes = ['Hydrant', 'Sign', 'Valve', 'Manhole', 'Water Line', 'Water Meter', 'Sewer Line'];
    const statuses = ['Open', 'Closed'];
    const createdUsers = ['John Smith', 'Jane Doe', 'Bonnie AI Assistant', 'Mike Johnson', 'Sarah Wilson'];
    
    const mockCalls = [];
    const currentDate = new Date();
    
    for (let i = 1; i <= 50; i++) {
      const randomDate = new Date(currentDate.getTime() - Math.random() * 365 * 24 * 60 * 60 * 1000);
      const randomCreatedUser = createdUsers[Math.floor(Math.random() * createdUsers.length)];
      mockCalls.push({
        id: i,
        dateReceived: randomDate.toISOString().split('T')[0],
        callerInformation: `Caller ${i} - ${Math.random() > 0.5 ? 'Resident' : 'Business Owner'}`,
        callerEmail: Math.random() > 0.3 ? `caller${i}@example.com` : '',
        callerPhone: Math.random() > 0.3 ? `555-${Math.floor(Math.random() * 900) + 100}-${Math.floor(Math.random() * 9000) + 1000}` : '',
        responsibleDepartment: departments[Math.floor(Math.random() * departments.length)],
        appliesTo: assetTypes[Math.floor(Math.random() * assetTypes.length)],
        locationDescription: `${Math.floor(Math.random() * 1000)} ${['Main St', 'Oak Ave', 'Pine Rd', 'Elm St'][Math.floor(Math.random() * 4)]}`,
        issueDetails: `Issue description for call ${i} - ${['Leak', 'Damage', 'Maintenance', 'Emergency'][Math.floor(Math.random() * 4)]} reported`,
        status: statuses[Math.floor(Math.random() * statuses.length)],
        inspections: [],
        isAIProcessed: randomCreatedUser.includes('AI Assistant')
      });
    }
    
    return mockCalls;
  };

  // Dashboard statistics
  const getYearToDateCalls = () => {
    const currentYear = new Date().getFullYear();
    return calls.filter(call => {
      const callYear = new Date(call.dateReceived).getFullYear();
      return callYear === currentYear;
    }).length;
  };

  const getLast30DaysCalls = () => {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    
    return calls.filter(call => {
      const callDate = new Date(call.dateReceived);
      return callDate >= thirtyDaysAgo;
    }).length;
  };

  const getCallsByDepartment = () => {
    const departmentCounts = {};
    calls.forEach(call => {
      departmentCounts[call.responsibleDepartment] = (departmentCounts[call.responsibleDepartment] || 0) + 1;
    });
    return departmentCounts;
  };

  // Chart options for department calls
  const getChartOptions = () => {
    const departmentData = getCallsByDepartment();
    const departments = Object.keys(departmentData);
    const counts = Object.values(departmentData);

    return {
      chart: {
        type: 'bar',
        height: 300,
        toolbar: {
          show: false
        }
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '55%',
          endingShape: 'rounded'
        },
      },
      dataLabels: {
        enabled: false
      },
      stroke: {
        show: true,
        width: 2,
        colors: ['transparent']
      },
      xaxis: {
        categories: departments,
        title: {
          text: 'Department'
        }
      },
      yaxis: {
        title: {
          text: 'Number of Calls'
        }
      },
      fill: {
        opacity: 1,
        colors: ['#4285f4', '#34a853', '#fbbc04', '#ea4335', '#9c27b0']
      },
      tooltip: {
        y: {
          formatter: function (val) {
            return val + " calls"
          }
        }
      }
    };
  };

  const getChartSeries = () => {
    const departmentData = getCallsByDepartment();
    const counts = Object.values(departmentData);
    
    return [{
      name: 'Calls',
      data: counts
    }];
  };

  // Table sorting
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Table pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Format date for display
  const formatDate = (dateString) => {
    const d = new Date(dateString);
    // Use UTC methods to avoid timezone conversion issues
    const year = d.getUTCFullYear();
    const month = String(d.getUTCMonth() + 1).padStart(2, '0');
    const day = String(d.getUTCDate()).padStart(2, '0');
    return `${month}/${day}/${year}`;
  };

  // Get status color
  const getStatusColor = (status) => {
    return status === 'Open' ? 'warning' : 'success';
  };

  // Validation function
  const validateForm = () => {
    const newErrors = {};
    
    if (!callData.callerInformation?.trim()) {
      newErrors.callerInformation = 'Caller information is required';
    }
    if (!callData.responsibleDepartment) {
      newErrors.responsibleDepartment = 'Responsible department is required';
    }
    if (!callData.locationDescription?.trim()) {
      newErrors.locationDescription = 'Location description is required';
    }
    if (!callData.issueDetails?.trim()) {
      newErrors.issueDetails = 'Issue details are required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Submit form
  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Prepare the data for API
      const apiData = {
        ClientID: user.ClientID,
        dateReceived: callData.dateReceived,
        callerInformation: callData.callerInformation,
        callerEmail: callData.callerEmail || '',
        callerPhone: callData.callerPhone || '',
        responsibleDepartment: callData.responsibleDepartment,
        appliesTo: callData.appliesTo,
        locationDescription: callData.locationDescription,
        issueDetails: callData.issueDetails,
        status: callData.status,
        Created_User: user.FirstName + ' ' + user.LastName,
        Created_Date: new Date().toISOString(),
        Last_Editor: user.FirstName + ' ' + user.LastName,
        Last_Edited_Date: new Date().toISOString()
      };

      let response;
      if (isEditMode) {
        // Update existing call
        apiData.CallDetailID = editingCallId;
        response = await fetch('/api/callUpdate', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(apiData)
        });
      } else {
        // Create new call
        response = await fetch('/api/callInsert', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(apiData)
        });
      }

      if (response.ok) {
        const result = await response.json();
        
        // Refresh the calls list
        await fetchCalls();
        
        // Call the callback to refresh the main calls page
        handleCallUpdated();
        
        // Reset form and close modal
        handleModalClose();
      } else {
        const errorData = await response.json();
        console.error('API Error:', errorData);
        alert('Error submitting call. Please try again.');
      }
      
    } catch (error) {
      console.error('Error submitting call:', error);
      alert('Error submitting call. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset form
  const handleReset = () => {
    setCallData({
      dateReceived: formatDateForInput(new Date()),
      callerInformation: '',
      callerEmail: '',
      callerPhone: '',
      responsibleDepartment: '',
      appliesTo: '',
      locationDescription: '',
      issueDetails: '',
      status: 'Open'
    });
    setInspections([]);
    setNewInspection({
      date: formatDateForInput(new Date()),
      descriptionOfWork: '',
      closeCall: false,
      overallCondition: 'Good'
    });
    setErrors({});
    setShowInspectionForm(false);
    setEditingInspectionIndex(null);
  };

  // Load existing call data for editing
  const loadCallForEditing = (call) => {
    setCallData({
      dateReceived: call.dateReceived ? formatDateForInput(new Date(call.dateReceived)) : formatDateForInput(new Date()),
      callerInformation: call.callerInformation,
      callerEmail: call.callerEmail || '',
      callerPhone: call.callerPhone || '',
      responsibleDepartment: call.responsibleDepartment,
      appliesTo: call.appliesTo,
      locationDescription: call.locationDescription,
      issueDetails: call.issueDetails,
      status: call.status
    });
    setInspections(call.inspections || []);
    setEditingCallId(call.CallDetailID);
    setIsEditMode(true);
    setErrors({});
    setShowInspectionForm(false);
    setEditingInspectionIndex(null);
  };

  // Fetch inspections for a specific call
  const fetchInspections = async (callDetailID) => {
    try {
      const response = await fetch('/api/callInspectionSelect', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ClientID: user.ClientID,
          CallDetailID: callDetailID
        })
      });

      if (response.ok) {
        const data = await response.json();
        // Transform the data to match our frontend structure
        const transformedInspections = data.result.map(inspection => ({
          id: inspection.InspectionID,
          InspectionID: inspection.InspectionID,
          date: inspection.InspectionDate,
          loggedBy: inspection.Inspector,
          descriptionOfWork: inspection.Comment,
          overallCondition: inspection.OverallCondition,
          closeCall: false // This will be determined by the overall condition or status
        }));
        setInspections(transformedInspections);
      } else {
        console.error('Failed to fetch inspections');
        setInspections([]);
      }
    } catch (error) {
      console.error('Error fetching inspections:', error);
      setInspections([]);
    }
  };

  // Create new inspection
  const createInspection = async (inspectionData) => {
    try {
      const response = await fetch('/api/callInspectionInsert', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ClientID: user.ClientID,
          CallDetailID: editingCallId,
          Inspector: user.FirstName + ' ' + user.LastName,
          InspectionDate: inspectionData.date,
          CreatedUser: user.FirstName + ' ' + user.LastName,
          CreatedDate: new Date().toISOString(),
          LastEditor: user.FirstName + ' ' + user.LastName,
          LastEditedDate: new Date().toISOString(),
          OverallCondition: inspectionData.overallCondition || 'Good',
          Comment: inspectionData.descriptionOfWork
        })
      });

      if (response.ok) {
        const data = await response.json();
        // Refresh inspections after creating new one
        await fetchInspections(editingCallId);
        return { success: true, data };
      } else {
        console.error('Failed to create inspection');
        return { success: false, error: 'Failed to create inspection' };
      }
    } catch (error) {
      console.error('Error creating inspection:', error);
      return { success: false, error: error.message };
    }
  };

  // Update existing inspection
  const updateInspection = async (inspectionData) => {
    try {
      const response = await fetch('/api/callInspectionUpdate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ClientID: user.ClientID,
          InspectionID: inspectionData.InspectionID,
          CallDetailID: editingCallId,
          Inspector: user.FirstName + ' ' + user.LastName,
          InspectionDate: inspectionData.date,
          CreatedUser: user.FirstName + ' ' + user.LastName,
          CreatedDate: new Date().toISOString(),
          LastEditor: user.FirstName + ' ' + user.LastName,
          LastEditedDate: new Date().toISOString(),
          OverallCondition: inspectionData.overallCondition || 'Good',
          Comment: inspectionData.descriptionOfWork
        })
      });

      if (response.ok) {
        const data = await response.json();
        // Refresh inspections after updating
        await fetchInspections(editingCallId);
        return { success: true, data };
      } else {
        console.error('Failed to update inspection');
        return { success: false, error: 'Failed to update inspection' };
      }
    } catch (error) {
      console.error('Error updating inspection:', error);
      return { success: false, error: error.message };
    }
  };

  // Delete inspection
  const deleteInspection = async (inspectionID) => {
    try {
      const response = await fetch('/api/callInspectionDelete', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ClientID: user.ClientID,
          InspectionID: inspectionID
        })
      });

      if (response.ok) {
        const data = await response.json();
        // Refresh inspections after deleting
        await fetchInspections(editingCallId);
        return { success: true, data };
      } else {
        console.error('Failed to delete inspection');
        return { success: false, error: 'Failed to delete inspection' };
      }
    } catch (error) {
      console.error('Error deleting inspection:', error);
      return { success: false, error: error.message };
    }
  };

  // Handle view/edit call
  const handleViewCall = (call) => {
    loadCallForEditing(call);
    setIsModalOpen(true);
    // Fetch inspections when modal opens
    fetchInspections(call.CallDetailID);
  };

  // Handle modal close
  const handleModalClose = () => {
    setIsModalOpen(false);
    setIsEditMode(false);
    setEditingCallId(null);
    handleReset();
  };

  // Handle department settings close
  const handleDepartmentSettingsClose = () => {
    setShowDepartmentSettings(false);
    // Refresh configuration after settings are closed
    fetchCallSystemConfig();
  };

  // Get unique asset types from existing calls
  const getUniqueAssetTypes = () => {
    const uniqueTypes = new Set();
    
    // Add configured asset types
    if (callSystemConfig?.AssetTypes) {
      callSystemConfig.AssetTypes.forEach(type => uniqueTypes.add(type));
    }
    
    // Add asset types from existing calls
    calls.forEach(call => {
      if (call.appliesTo && call.appliesTo.trim()) {
        uniqueTypes.add(call.appliesTo.trim());
      }
    });
    
    // Convert to array and sort
    return Array.from(uniqueTypes).sort();
  };

  // Callback to refresh calls when a call is updated
  const handleCallUpdated = () => {
    fetchCalls();
  };

  // Returns true if the given department has at least one email in config
  const departmentHasEmails = (departmentName) => {
    if (!callSystemConfig?.Departments?.length || !departmentName) return false;
    const dept = callSystemConfig.Departments.find(d =>
      typeof d === 'string' ? d === departmentName : d?.name === departmentName
    );
    if (!dept || typeof dept === 'string') return false;
    return Array.isArray(dept.emails) && dept.emails.length > 0;
  };

  // Handle resend email functionality
  const handleResendEmail = (call) => {
    setResendingCall(call);
    setResendEmailDialog(true);
    setResendEmailResult(null);
  };

  const confirmResendEmail = async () => {
    if (!resendingCall) return;
    
    setResendingEmail(true);
    try {
      const response = await fetch('/api/resendDepartmentEmail', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ClientID: user.ClientID,
          CallDetailID: resendingCall.CallDetailID,
          responsibleDepartment: resendingCall.responsibleDepartment
        })
      });

      if (response.ok) {
        const result = await response.json();
        setResendEmailResult({
          success: true,
          message: result.message || 'Department notification email resent successfully!'
        });
      } else {
        const errorData = await response.json();
        setResendEmailResult({
          success: false,
          message: errorData.error || 'Failed to resend email. Please try again.'
        });
      }
    } catch (error) {
      console.error('Error resending email:', error);
      setResendEmailResult({
        success: false,
        message: 'Error resending email. Please check your connection and try again.'
      });
    } finally {
      setResendingEmail(false);
    }
  };

  const closeResendEmailDialog = () => {
    setResendEmailDialog(false);
    setResendingCall(null);
    setResendEmailResult(null);
  };

  return (
    <div style={{ overflowX: "hidden", height: "100vh", background: "white" }}>
      <NavBar isAccountScreen={true} />
      <div ref={topOfPageRef} style={{ padding: "80px 30px 30px 30px" }}>
        <Box 
          display="flex" 
          justifyContent="space-between" 
          alignItems="center" 
          marginBottom="20px"
          className="sticky-header"
        >
          {/* no margin bottom for mobile */}
          <h1 style={{ width: "100%", marginBottom: { xs: "0px", sm: "20px" } }}>
            {callSystemConfig?.CityName ? `${callSystemConfig.CityName} Calls` : 'Calls'}
          </h1>
          <Box display="flex" gap={2}>
            <IconButton
              onClick={fetchCalls}
              disabled={loading}
              sx={{ width: '40px', height: '40px' }}
            >
              {loading ? (
                <CircularProgress size={20} />
              ) : (
                <RefreshIcon />
              )}
            </IconButton>
            <Button
              variant="outlined"
              startIcon={<SettingsIcon />}
              onClick={() => {
                setShowDepartmentSettings(!showDepartmentSettings);
                // Scroll to top of page when opening department settings
                if (!showDepartmentSettings) {
                  // Try multiple scroll methods to ensure it works
                  setTimeout(() => {
                    // Method 1: Try React ref scrollIntoView (most reliable)
                    if (topOfPageRef.current) {
                      topOfPageRef.current.scrollIntoView({ 
                        behavior: 'smooth', 
                        block: 'start' 
                      });
                    }
                    
                    // Method 2: Try window.scrollTo
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                    
                    // Method 3: Try document.documentElement.scrollTop
                    document.documentElement.scrollTop = 0;
                    
                    // Method 4: Try document.body.scrollTop
                    document.body.scrollTop = 0;
                    
                    // Method 5: Try scrolling the main container
                    const mainContainer = document.querySelector('div[style*="height: 100vh"]');
                    if (mainContainer) {
                      mainContainer.scrollTop = 0;
                    }
                    
                    // Method 6: Try finding and scrolling any scrollable parent
                    const scrollableElements = document.querySelectorAll('[style*="overflow"], [style*="scroll"]');
                    scrollableElements.forEach((el, index) => {
                      if (el.scrollTop > 0) {
                        el.scrollTop = 0;
                      }
                    });
                    
                  }, 100);
                }
              }}
              sx={{ 
                width: { xs: '40px', sm: '180px' },
                minWidth: { xs: '40px', sm: '180px' },
                padding: { xs: '0px 28px', sm: '0px 6px' },
                '& .MuiButton-startIcon': {
                  margin: { xs: 0, sm: 'auto' }
                }
              }}
            >
              <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                Departments
              </Box>
              {showDepartmentSettings ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setIsEditMode(false);
                setEditingCallId(null);
                handleReset();
                setIsModalOpen(true);
              }}
              sx={{ 
                backgroundColor: '#4285f4', 
                width: { xs: '40px', sm: '160px' },
                minWidth: { xs: '40px', sm: '160px' },
                '& .MuiButton-startIcon': {
                  margin: { xs: 0, sm: 'auto' }
                }
              }}
            >
              <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                New Call
              </Box>
            </Button>
          </Box>
        </Box>

        {/* Department Settings Panel */}
        <Collapse in={showDepartmentSettings}>
          <Box mb={3}>
            <DepartmentSettings 
              user={user} 
              onClose={handleDepartmentSettingsClose}
            />
          </Box>
        </Collapse>

        {/* Department Configuration Status Card */}
        {callSystemConfig && (
          <Card sx={{ mb: 3, backgroundColor: '#f8f9fa' }}>
            <CardContent>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={6}>
                  <Box display="flex" alignItems="center">
                    <ListIcon sx={{ mr: 1, color: '#4285f4' }} />
                    <Typography variant="h6">
                      Department Configuration
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="textSecondary">
                    {callSystemConfig.Departments?.length || 0} departments configured
                  </Typography>
                  {callSystemConfig.CallModuleEnabled === false && (
                    <Typography variant="caption" color="textSecondary" display="block">
                      AI Call System: Disabled
                    </Typography>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box display="flex" justifyContent="flex-end" gap={1}>
                    <Chip 
                      label={`${callSystemConfig.Departments?.length || 0} Departments`} 
                      color="primary" 
                      size="small"
                    />
                    {callSystemConfig.CallModuleEnabled === false && (
                      <Chip 
                        label="AI Disabled" 
                        color="default" 
                        size="small"
                      />
                    )}
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Dashboard Tiles */}
        <Grid container spacing={3} marginBottom={4}>
          {/* Year to Date Calls */}
          <Grid item xs={12} sm={6} md={3}>
            <Card className="dashboard-card">
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="textSecondary" gutterBottom variant="h6">
                      Year to Date
                    </Typography>
                    <Typography variant="h4" component="div">
                      {getYearToDateCalls()}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Total calls this year
                    </Typography>
                  </Box>
                  <PhoneIcon sx={{ fontSize: 40, color: '#4285f4' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Last 30 Days */}
          <Grid item xs={12} sm={6} md={3}>
            <Card className="dashboard-card">
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="textSecondary" gutterBottom variant="h6">
                      Last 30 Days
                    </Typography>
                    <Typography variant="h4" component="div">
                      {getLast30DaysCalls()}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Recent activity
                    </Typography>
                  </Box>
                  <CalendarIcon sx={{ fontSize: 40, color: '#34a853' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Open Calls */}
          <Grid item xs={12} sm={6} md={3}>
            <Card className="dashboard-card">
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="textSecondary" gutterBottom variant="h6">
                      Open Calls
                    </Typography>
                    <Typography variant="h4" component="div">
                      {calls.filter(call => call.status === 'Open').length}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Pending resolution
                    </Typography>
                  </Box>
                  <TrendingUpIcon sx={{ fontSize: 40, color: '#fbbc04' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Closed Calls */}
          <Grid item xs={12} sm={6} md={3}>
            <Card className="dashboard-card">
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="textSecondary" gutterBottom variant="h6">
                      Closed Calls
                    </Typography>
                    <Typography variant="h4" component="div">
                      {calls.filter(call => call.status === 'Closed').length}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      Resolved issues
                    </Typography>
                  </Box>
                  <TrendingUpIcon sx={{ fontSize: 40, color: '#ea4335' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Department Chart */}
        <Card className="chart-card" marginBottom={4}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Calls by Department
            </Typography>
            <Chart
              options={getChartOptions()}
              series={getChartSeries()}
              type="bar"
              height={300}
            />
          </CardContent>
        </Card>

        {/* Calls Table */}
        <Card className="table-card">
          <CardContent>
            <Typography variant="h6" gutterBottom>
              All Calls
            </Typography>
            
            {/* Filters */}
            <Box display="flex" gap={2} marginBottom={2} flexWrap="wrap">
              <TextField
                placeholder="Search calls..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                size="small"
                sx={{ minWidth: 200 }}
              />
              
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">All Statuses</MenuItem>
                  <MenuItem value="Open">Open</MenuItem>
                  <MenuItem value="Closed">Closed</MenuItem>
                </Select>
              </FormControl>
              
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Department</InputLabel>
                <Select
                  value={departmentFilter}
                  label="Department"
                  onChange={(e) => setDepartmentFilter(e.target.value)}
                >
                  <MenuItem value="all">All Departments</MenuItem>
                  {callSystemConfig?.Departments?.map(dept => (
                    <MenuItem key={typeof dept === 'string' ? dept : dept.name} value={typeof dept === 'string' ? dept : dept.name}>
                      {typeof dept === 'string' ? dept : dept.name}
                    </MenuItem>
                  )) || [
                    <MenuItem key="public-works" value="Public Works">Public Works</MenuItem>,
                    <MenuItem key="streets" value="Streets">Streets</MenuItem>,
                    <MenuItem key="water" value="Water">Water</MenuItem>,
                    <MenuItem key="parks" value="Parks">Parks</MenuItem>,
                    <MenuItem key="fire" value="Fire Department">Fire Department</MenuItem>
                  ]}
                </Select>
              </FormControl>

              {/* <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Call Source</InputLabel>
                <Select
                  value={callSourceFilter}
                  label="Call Source"
                  onChange={(e) => setCallSourceFilter(e.target.value)}
                >
                  <MenuItem value="all">All Sources</MenuItem>
                  <MenuItem value="ai">AI Processed</MenuItem>
                  <MenuItem value="manual">Manual Entry</MenuItem>
                </Select>
              </FormControl> */}
            </Box>

            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'dateReceived'}
                        direction={orderBy === 'dateReceived' ? order : 'asc'}
                        onClick={() => handleRequestSort('dateReceived')}
                      >
                        Date Received
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Caller Information</TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'responsibleDepartment'}
                        direction={orderBy === 'responsibleDepartment' ? order : 'asc'}
                        onClick={() => handleRequestSort('responsibleDepartment')}
                      >
                        Department
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Asset Type</TableCell>
                    <TableCell>Location</TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={orderBy === 'status'}
                        direction={orderBy === 'status' ? order : 'asc'}
                        onClick={() => handleRequestSort('status')}
                      >
                        Status
                      </TableSortLabel>
                    </TableCell>
                    {/* <TableCell>Source</TableCell> */}
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Typography>Loading calls...</Typography>
                      </TableCell>
                    </TableRow>
                  ) : filteredCalls.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Typography>No calls found</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredCalls
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((call) => (
                      <TableRow key={call.id} hover>
                        <TableCell>{formatDate(call.dateReceived)}</TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                            {call.callerInformation}
                          </Typography>
                        </TableCell>
                        <TableCell>{call.responsibleDepartment}</TableCell>
                        <TableCell>{call.appliesTo}</TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                            {call.locationDescription}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={call.status}
                            color={getStatusColor(call.status)}
                            size="small"
                          />
                        </TableCell>
                        {/* <TableCell>
                          {call.isAIProcessed ? (
                            <Chip
                              icon={<AIIcon />}
                              label="AI"
                              color="info"
                              size="small"
                              variant="outlined"
                            />
                          ) : (
                            <Chip
                              label="Manual"
                              color="default"
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </TableCell> */}
                        <TableCell>
                          <Box display="flex" gap={1}>
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => handleViewCall(call)}
                            >
                              View
                            </Button>
                            <Tooltip
                              title={
                                departmentHasEmails(call.responsibleDepartment)
                                  ? 'Resend department notification email'
                                  : 'No email addresses configured for this department. Add them in Department Settings.'
                              }
                            >
                              <span>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="secondary"
                                  startIcon={<SendIcon />}
                                  onClick={() => handleResendEmail(call)}
                                  disabled={call.status === 'Closed' || !departmentHasEmails(call.responsibleDepartment)}
                                  sx={{ minWidth: 'auto', px: 1 }}
                                >
                                  <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                                    Resend
                                  </Box>
                                </Button>
                              </span>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={filteredCalls.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </CardContent>
        </Card>
      </div>

      {/* Call Tracking Form Modal */}
      <Modal
        open={isModalOpen}
        onClose={handleModalClose}
        aria-labelledby="call-tracking-modal"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
          zIndex: 9999
        }}
      >
        <Box
          sx={{
            width: '95%',
            maxWidth: 1200,
            maxHeight: '95vh',
            bgcolor: 'background.paper',
            boxShadow: 24,
            overflow: 'auto',
            zIndex: 10000
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 2,
              borderBottom: 1,
              borderColor: 'divider',
              backgroundColor: '#343a40',
              color: 'white'
            }}
            style={{ borderBottomLeftRadius: '0px', borderBottomRightRadius: '0px', borderTopRightRadius: '0px' }}
          >
            <Typography variant="h5" gutterBottom className="call-tracking-title" sx={{ color: 'white', margin: 0 }}>
              {isEditMode ? 'Edit Call Tracking' : 'New Call Tracking'}
            </Typography>
            <Button onClick={handleModalClose} sx={{ color: 'red' }}>Close</Button>
          </Box>
          <Box sx={{ p: 2 }}>
            <CallTrackingForm
              callData={callData}
              setCallData={setCallData}
              inspections={inspections}
              setInspections={setInspections}
              newInspection={newInspection}
              setNewInspection={setNewInspection}
              showInspectionForm={showInspectionForm}
              setShowInspectionForm={setShowInspectionForm}
              editingInspectionIndex={editingInspectionIndex}
              setEditingInspectionIndex={setEditingInspectionIndex}
              errors={errors}
              setErrors={setErrors}
              onSubmit={handleSubmit}
              onReset={handleReset}
              isSubmitting={isSubmitting}
              isEditMode={isEditMode}
              configuredDepartments={callSystemConfig?.Departments || []}
              configuredAssetTypes={getUniqueAssetTypes()}
              createInspection={createInspection}
              updateInspection={updateInspection}
              deleteInspection={deleteInspection}
              editingCallId={editingCallId}
              user={user}
              onCallUpdated={handleCallUpdated}
            />
          </Box>
        </Box>
      </Modal>

      {/* Deep Link Notification */}
      <Snackbar
        open={showDeepLinkNotification}
        autoHideDuration={5000}
        onClose={() => setShowDeepLinkNotification(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setShowDeepLinkNotification(false)} 
          severity="info" 
          variant="filled"
          sx={{ width: '100%' }}
        >
          Service request opened from email notification. You can now view details and add resolution notes.
        </Alert>
      </Snackbar>

      {/* Resend Email Confirmation Dialog */}
      <Dialog
        open={resendEmailDialog}
        onClose={closeResendEmailDialog}
        aria-labelledby="resend-email-dialog-title"
        aria-describedby="resend-email-dialog-description"
      >
        <DialogTitle id="resend-email-dialog-title">
          <Box display="flex" alignItems="center" gap={1}>
            <EmailIcon color="secondary" />
            Resend Department Notification
          </Box>
        </DialogTitle>
        <DialogContent>
          {resendEmailResult ? (
            <Alert severity={resendEmailResult.success ? "success" : "error"} sx={{ mb: 2 }}>
              {resendEmailResult.message}
            </Alert>
          ) : (
            <DialogContentText id="resend-email-dialog-description">
              {resendingCall && (
                <>
                  Are you sure you want to resend the department notification email for this service request?
                  <br /><br />
                  <strong>Call ID:</strong> {resendingCall.CallDetailID}<br />
                  <strong>Department:</strong> {resendingCall.responsibleDepartment}<br />
                  <strong>Issue:</strong> {resendingCall.issueDetails}<br />
                  <strong>Location:</strong> {resendingCall.locationDescription}
                </>
              )}
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          {resendEmailResult ? (
            <Button onClick={closeResendEmailDialog} color="primary">
              Close
            </Button>
          ) : (
            <>
              <Button onClick={closeResendEmailDialog} color="inherit">
                Cancel
              </Button>
              <Button
                onClick={confirmResendEmail}
                color="secondary"
                variant="contained"
                disabled={resendingEmail}
                startIcon={resendingEmail ? <CircularProgress size={16} /> : <SendIcon />}
              >
                {resendingEmail ? 'Sending...' : 'Resend Email'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default CallsPage;
